import type { PropsWithChildren, ReactNode } from 'react';
import { useRouter } from '@tanstack/react-router';
import { PageLoader } from '@customerio/pluma-components/react';
import Journeys from '@/pages/journeys';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { JourneysHandoffView } from '@/services/router/journeys-handoff-view';
import { MigrationRouteView } from '@/services/router/migration-route-view';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { useMigrationHandoffHref } from '@/services/router/use-migration-handoff-href';
import { generatePath } from '@/services/router/generate-path';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { ProtectedRoute } from '@/services/router/components/protected-route';
import { RumPageRendered } from '@/services/rum/use-page-rendered';
import PeopleListPage from './index.page';
import PeopleCreatePage from './create/index.page';
import PeopleNewPage from './new/index.page';
import PeopleMergePage from './merge/index.page';

const LOADING_LABEL = 'Loading…';

/**
 * The segment a migrated People URL was reached at. Both spellings ship; `people` is the
 * canonical one and `profiles` forwards to it.
 */
export type PeopleUrlSegment = 'people' | 'profiles';

/** The segment the migrated People URLs are served at. */
const CANONICAL_SEGMENT: PeopleUrlSegment = 'people';

/** The migrated People URL each key names. */
const PEOPLE_ROUTES = {
  'people.list': '/workspaces/:workspaceId/people',
  'people.create': '/workspaces/:workspaceId/people/create',
  'people.new': '/workspaces/:workspaceId/people/new',
  'people.merge': '/workspaces/:workspaceId/people/merge',
} as const;

/** The keys for the migrated People URLs. */
type PeopleRouteKey = keyof typeof PEOPLE_ROUTES;

/** The `journeys` twins Ember serves for the migrated People pages. */
type LegacyPeopleTarget =
  | '/workspaces/$workspaceId/journeys/people'
  | '/workspaces/$workspaceId/journeys/people/create'
  | '/workspaces/$workspaceId/journeys/people/new'
  | '/workspaces/$workspaceId/journeys/people/merge'
  | '/workspaces/$workspaceId/journeys/people/welcome';

type PeopleRouteViewProps = {
  segment: PeopleUrlSegment;
};

/**
 * Shared body of the `journeys` handoffs below: flag off renders the embedded Ember host,
 * flag on forwards to the migrated URL.
 */
function useJourneysHandoff(routeKey: PeopleRouteKey) {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.ProfilesMigration);
  const migratedHref = useMigratedPeopleHref(routeKey);

  return (
    <JourneysHandoffView isMigrated={isMigrated} migratedHref={migratedHref}>
      <Journeys />
    </JourneysHandoffView>
  );
}

/** The migrated URL, with the query string carried across; `null` while the workspace resolves. */
function useMigratedPeopleHref(routeKey: PeopleRouteKey): string | null {
  const template = PEOPLE_ROUTES[routeKey];

  return useMigrationHandoffHref((workspaceId) => template.replace(':workspaceId', workspaceId));
}

/** The `journeys` twin's URL, with the query string carried across; `null` while the workspace resolves. */
function useLegacyPeopleHref(legacyTarget: LegacyPeopleTarget): string | null {
  const router = useRouter();

  return useMigrationHandoffHref(
    (workspaceId) => router.buildLocation({ to: legacyTarget, params: { workspaceId } }).href,
  );
}

type CanonicalSpellingHandoffProps = PropsWithChildren<{
  /** Whether the URL was reached at the canonical segment. */
  isCanonical: boolean;
  /** The canonical URL; `null` while the workspace resolves. */
  canonicalHref: string | null;
}>;

/**
 * Holds a migrated People page to its canonical spelling: the other spelling forwards to it,
 * replacing history, so the two never disagree in the address bar.
 */
function CanonicalSpellingHandoff({ isCanonical, canonicalHref, children }: CanonicalSpellingHandoffProps) {
  if (isCanonical) {
    return children;
  }

  if (canonicalHref == null) {
    return <PageLoader label={LOADING_LABEL} />;
  }

  return <SafeNavigate to={canonicalHref} options={{ shouldReplaceHistoryEntry: true }} />;
}

/** The old `/journeys/people` and `/journeys/profiles` URLs. */
export function JourneysPeopleHandoff() {
  return useJourneysHandoff('people.list');
}

/**
 * The migrated People list, served at `/people` and `/profiles`. Flag on → Hydra owns the page,
 * at the canonical `/people` segment. Flag off → this URL has no Ember counterpart (Ember only
 * knows the `journeys` forms), so hand back to the `journeys` twin, which Ember serves
 * (canonicalizing the segment itself) — the exact inverse of
 * {@link JourneysPeopleHandoff}. The two can't loop: each fires in the opposite flag state.
 */
export function PeopleListRouteView({ segment }: PeopleRouteViewProps) {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.ProfilesMigration);
  const legacyHref = useLegacyPeopleHref('/workspaces/$workspaceId/journeys/people');
  const canonicalHref = useMigratedPeopleHref('people.list');
  // Readiness gates on the resolved workspace, not the URL segment: the
  // page's reads go through `requireWorkspaceId`, which throws while the
  // workspaces list is still loading.
  const workspace = useCurrentWorkspace();

  return (
    <MigrationRouteView isMigrated={isMigrated} legacyHref={legacyHref} isReady={workspace != null}>
      <CanonicalSpellingHandoff isCanonical={segment === CANONICAL_SEGMENT} canonicalHref={canonicalHref}>
        <RumPageRendered>
          <PeopleListPage />
        </RumPageRendered>
      </CanonicalSpellingHandoff>
    </MigrationRouteView>
  );
}

/**
 * The old `/journeys/people/create` and `/journeys/profiles/create` URLs, the
 * add-people twins of {@link JourneysPeopleHandoff}.
 */
export function JourneysPeopleCreateHandoff() {
  return useJourneysHandoff('people.create');
}

type PeopleChildRouteViewProps = PeopleRouteViewProps & {
  /** The key for this URL's migrated target. */
  routeKey: 'people.create' | 'people.new';
  /** The `journeys` twin this URL hands back to while the flag is off. */
  legacyTarget: LegacyPeopleTarget;
  children: ReactNode;
};

/**
 * The shell of a migrated People child URL (`/people/create` and `/people/new`, and their
 * `profiles` spellings). Flag off it hands back to the `journeys` twin that Ember serves, the
 * inverse of the `journeys` handoffs above. Flag on, the page renders at the canonical
 * spelling, behind the same gate Ember's routes apply:
 * `redirectIfNoPermission(this, 'journeys/people/create')`, which sends a denied viewer to the
 * People list.
 */
function PeopleChildRouteView({ segment, routeKey, legacyTarget, children }: PeopleChildRouteViewProps) {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.ProfilesMigration);
  const legacyHref = useLegacyPeopleHref(legacyTarget);
  const canonicalHref = useMigratedPeopleHref(routeKey);
  const workspace = useCurrentWorkspace();

  return (
    <MigrationRouteView isMigrated={isMigrated} legacyHref={legacyHref} isReady={workspace != null}>
      <CanonicalSpellingHandoff isCanonical={segment === CANONICAL_SEGMENT} canonicalHref={canonicalHref}>
        {/*
          Redirect target is built from the resolved workspace rather than `ProtectedRoute`'s route
          params: `MigrationRouteView` already gated on that workspace above, and the `last` sentinel
          is only a fallback for the window before it resolves.
        */}
        <ProtectedRoute
          permission="journeys/people/create"
          redirectTo={generatePath('/workspaces/:workspaceId/people', { workspaceId: workspace?.id ?? 'last' })}
        >
          {children}
        </ProtectedRoute>
      </CanonicalSpellingHandoff>
    </MigrationRouteView>
  );
}

/** The migrated add-people catalog, served at `/people/create` and `/profiles/create`. */
export function PeopleCreateRouteView({ segment }: PeopleRouteViewProps) {
  return (
    <PeopleChildRouteView
      segment={segment}
      routeKey="people.create"
      legacyTarget="/workspaces/$workspaceId/journeys/people/create"
    >
      <RumPageRendered>
        <PeopleCreatePage />
      </RumPageRendered>
    </PeopleChildRouteView>
  );
}

/**
 * The old `/journeys/people/new` and `/journeys/profiles/new` URLs — the
 * add-a-person twins of {@link JourneysPeopleHandoff}. The add-people catalog
 * in the embedded app still links here.
 */
export function JourneysPeopleNewHandoff() {
  return useJourneysHandoff('people.new');
}

/** The migrated add-a-person form, served at `/people/new` and `/profiles/new`. */
export function PeopleNewRouteView({ segment }: PeopleRouteViewProps) {
  return (
    <PeopleChildRouteView
      segment={segment}
      routeKey="people.new"
      legacyTarget="/workspaces/$workspaceId/journeys/people/new"
    >
      <RumPageRendered>
        <PeopleNewPage />
      </RumPageRendered>
    </PeopleChildRouteView>
  );
}

/**
 * The old `/journeys/people/merge` and `/journeys/profiles/merge` URLs — the
 * merge-wizard twins of {@link JourneysPeopleHandoff}. The embedded app's person
 * page and activity log, and the copilot's suggestions, still link here.
 */
export function JourneysPeopleMergeHandoff() {
  return useJourneysHandoff('people.merge');
}

/**
 * The migrated merge-two-people wizard, served at `/people/merge` and `/profiles/merge`. Unlike
 * the add-people pages there is no permission gate at the route: a viewer without
 * `journeys/people/edit` sees the wizard with its controls disabled.
 */
export function PeopleMergeRouteView({ segment }: PeopleRouteViewProps) {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.ProfilesMigration);
  const legacyHref = useLegacyPeopleHref('/workspaces/$workspaceId/journeys/people/merge');
  const canonicalHref = useMigratedPeopleHref('people.merge');
  const workspace = useCurrentWorkspace();

  return (
    <MigrationRouteView isMigrated={isMigrated} legacyHref={legacyHref} isReady={workspace != null}>
      <CanonicalSpellingHandoff isCanonical={segment === CANONICAL_SEGMENT} canonicalHref={canonicalHref}>
        <RumPageRendered>
          <PeopleMergePage />
        </RumPageRendered>
      </CanonicalSpellingHandoff>
    </MigrationRouteView>
  );
}

/**
 * The old `/journeys/people/welcome` and `/journeys/profiles/welcome` URLs.
 * Ember's `people.welcome` route is a bare `replaceWith('people')`, so both
 * spellings forward straight to the People list.
 */
export function JourneysPeopleWelcomeHandoff() {
  return useJourneysHandoff('people.list');
}

/**
 * The migrated `welcome` URL, served at `/people/welcome` and `/profiles/welcome`. There is no
 * welcome screen: like Ember's route it replaces itself with the People list. Flag off it hands
 * back to the `journeys` twin, which Ember serves and redirects the same way. Either way this URL
 * only ever redirects, so it is a single replace-navigate to whichever target the flag selects
 * rather than a `MigrationRouteView` around a page — and it takes no `segment`, because the list
 * it leaves for is already the canonical spelling whichever spelling it was reached at.
 */
export function PeopleWelcomeRouteView() {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.ProfilesMigration);
  const migratedHref = useMigratedPeopleHref('people.list');
  const legacyHref = useLegacyPeopleHref('/workspaces/$workspaceId/journeys/people/welcome');
  const to = isMigrated ? migratedHref : legacyHref;

  if (to == null) {
    return <PageLoader label={LOADING_LABEL} />;
  }

  return <SafeNavigate to={to} options={{ shouldReplaceHistoryEntry: true }} />;
}
